import {
  AllowedCharactersError,
  MissingStateInformationError,
  MissingZeroError,
  ParseError,
  TooLongError,
  TooShortError,
  WrongLengthForStateError,
  WrongStateError,
} from "./errors";
import {
  getStatesFromDigits,
  getStatesPrefix,
  statesWith10Digits,
  statesWith11Digits,
} from "./stateMapping";
import type { Options, ParsedSteuernummer } from "./types";

interface MatchedGroups {
  [key: string]: string;
}

/** Extracts named groups from a regex match, throwing ParseError if groups are missing */
function extractGroups(match: RegExpMatchArray | null): MatchedGroups {
  if (!match?.groups) {
    throw new ParseError();
  }
  return match.groups as unknown as MatchedGroups;
}

/** Gets a required group value, throwing ParseError if missing */
function g(matched: MatchedGroups, key: string): string {
  const value = matched[key];
  if (value === undefined) throw new ParseError();
  return value;
}

function resolveStates(
  options: Options,
  states: import("./types").StateCode[]
): import("./types").StateCode | import("./types").StateCode[] {
  if (options.bundesland) return options.bundesland;
  if (states.length === 1) {
    const first = states[0];
    if (first !== undefined) return first;
  }
  return states;
}

export function parseSteuernummer(
  value: string,
  options: Options
): ParsedSteuernummer {
  if (!/^[0-9/\s]*$/m.test(value)) {
    throw new AllowedCharactersError();
  }

  const digits = value.replace(/[^0-9]/gi, "");

  if (digits.length < 10) {
    throw new TooShortError();
  }
  if (digits.length > 13) {
    throw new TooLongError();
  }

  if (digits.length === 13) {
    return parseElectronicFormat(digits, options);
  }
  if (digits.length === 12) {
    return parseNationalFormat(digits, options);
  }
  return parseStateFormat(digits, options);
}

/** 13-digit "Vereinheitlichtes Bundesschema zur elektronischen Übermittlung" */
function parseElectronicFormat(
  digits: string,
  options: Options
): ParsedSteuernummer {
  const states = getStatesFromDigits(digits);

  if (
    options.bundesland !== undefined &&
    !states.includes(options.bundesland)
  ) {
    throw new WrongStateError(options.bundesland, states);
  }

  if (digits.charAt(4) !== "0") {
    throw new MissingZeroError();
  }

  const isNw = states.includes("DE-NW");
  const matched = extractGroups(
    digits.match(
      isNw
        ? /(?<bundesfinanzamtnummer>\d{4})(?<z>0)(?<bezirksnummer>\d{4})(?<unterscheidungsnummer>\d{3})(?<pruefziffer>\d{1})/
        : /(?<bundesfinanzamtnummer>\d{4})(?<z>0)(?<bezirksnummer>\d{3})(?<unterscheidungsnummer>\d{4})(?<pruefziffer>\d{1})/
    )
  );

  return {
    bezirksnummer: g(matched, "bezirksnummer"),
    bundesfinanzamtnummer: g(matched, "bundesfinanzamtnummer"),
    states: resolveStates(options, states),
    statePrefix: getStatesPrefix(states),
    normalizedSteuernummer: digits,
    pruefziffer: g(matched, "pruefziffer"),
    unterscheidungsnummer: g(matched, "unterscheidungsnummer"),
  };
}

/** 12-digit "Vereinheitlichtes Bundesschema" */
function parseNationalFormat(
  digits: string,
  options: Options
): ParsedSteuernummer {
  const states = getStatesFromDigits(digits);

  if (
    options.bundesland !== undefined &&
    !states.includes(options.bundesland)
  ) {
    throw new WrongStateError(options.bundesland, states);
  }

  const isNw = states.includes("DE-NW");
  const matched = extractGroups(
    digits.match(
      isNw
        ? /(?<bundesfinanzamtnummer>\d{4})(?<bezirksnummer>\d{4})(?<unterscheidungsnummer>\d{3})(?<pruefziffer>\d{1})/
        : /(?<bundesfinanzamtnummer>\d{4})(?<bezirksnummer>\d{3})(?<unterscheidungsnummer>\d{4})(?<pruefziffer>\d{1})/
    )
  );

  return {
    bezirksnummer: g(matched, "bezirksnummer"),
    bundesfinanzamtnummer: g(matched, "bundesfinanzamtnummer"),
    states: resolveStates(options, states),
    statePrefix: getStatesPrefix(states),
    normalizedSteuernummer: `${digits.substring(0, 4)}0${digits.substring(4, 12)}`,
    pruefziffer: g(matched, "pruefziffer"),
    unterscheidungsnummer: g(matched, "unterscheidungsnummer"),
  };
}

/** 10-11 digit "Standardschema der Länder" */
function parseStateFormat(
  digits: string,
  options: Options
): ParsedSteuernummer {
  const bundesland = options.bundesland;
  if (bundesland === undefined) {
    throw new MissingStateInformationError();
  }

  if (statesWith10Digits.has(bundesland) && digits.length !== 10) {
    throw new WrongLengthForStateError(bundesland);
  }
  if (statesWith11Digits.has(bundesland) && digits.length !== 11) {
    throw new WrongLengthForStateError(bundesland);
  }

  switch (bundesland) {
    case "DE-BW":
    case "DE-BE":
    case "DE-HB":
    case "DE-HH":
    case "DE-NI":
    case "DE-RP":
    case "DE-SH": {
      // Format: FF/BBB/UUUUP (10 digits)
      const m = extractGroups(
        digits.match(/(?<ff>\d{2})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/)
      );
      const statePrefix = getStatesPrefix([bundesland]);
      const ff = g(m, "ff");
      const bbb = g(m, "bbb");
      const uuuu = g(m, "uuuu");
      const p = g(m, "p");
      return {
        bezirksnummer: bbb,
        bundesfinanzamtnummer: statePrefix + ff,
        normalizedSteuernummer: `${statePrefix}${ff}0${bbb}${uuuu}${p}`,
        unterscheidungsnummer: uuuu,
        pruefziffer: p,
        statePrefix,
        states: bundesland,
      };
    }
    case "DE-BY":
    case "DE-BB":
    case "DE-MV":
    case "DE-SL":
    case "DE-SN":
    case "DE-ST":
    case "DE-TH": {
      // Format: FFF/BBB/UUUUP (11 digits)
      const m = extractGroups(
        digits.match(/(?<fff>\d{3})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/)
      );
      const statePrefix = getStatesPrefix([bundesland]);
      const fff = g(m, "fff");
      const bbb = g(m, "bbb");
      const uuuu = g(m, "uuuu");
      const p = g(m, "p");
      return {
        bezirksnummer: bbb,
        bundesfinanzamtnummer: statePrefix + fff,
        normalizedSteuernummer: `${statePrefix}${fff}0${bbb}${uuuu}${p}`,
        unterscheidungsnummer: uuuu,
        pruefziffer: p,
        statePrefix,
        states: bundesland,
      };
    }
    case "DE-HE": {
      // Format: 0FF/BBB/UUUUP (11 digits, leading 0)
      const m = extractGroups(
        digits.match(/0(?<ff>\d{2})(?<bbb>\d{3})(?<uuuu>\d{4})(?<p>\d{1})/)
      );
      const statePrefix = getStatesPrefix([bundesland]);
      const ff = g(m, "ff");
      const bbb = g(m, "bbb");
      const uuuu = g(m, "uuuu");
      const p = g(m, "p");
      return {
        bezirksnummer: bbb,
        bundesfinanzamtnummer: statePrefix + ff,
        normalizedSteuernummer: `${statePrefix}${ff}0${bbb}${uuuu}${p}`,
        unterscheidungsnummer: uuuu,
        pruefziffer: p,
        statePrefix,
        states: bundesland,
      };
    }
    case "DE-NW": {
      // Format: FFF/BBBB/UUUP (11 digits, 4-digit Bezirksnummer)
      const m = extractGroups(
        digits.match(/(?<fff>\d{3})(?<bbbb>\d{4})(?<uuu>\d{3})(?<p>\d{1})/)
      );
      const statePrefix = getStatesPrefix([bundesland]);
      const fff = g(m, "fff");
      const bbbb = g(m, "bbbb");
      const uuu = g(m, "uuu");
      const p = g(m, "p");
      return {
        bezirksnummer: bbbb,
        bundesfinanzamtnummer: statePrefix + fff,
        normalizedSteuernummer: `${statePrefix}${fff}0${bbbb}${uuu}${p}`,
        unterscheidungsnummer: uuu,
        pruefziffer: p,
        statePrefix,
        states: bundesland,
      };
    }
    default:
      throw new ParseError();
  }
}
